import { crmApiClient } from './crm-client';
import { crmAuth } from '../auth/crm-auth';
import type {
  IntakeDocumentType,
  IntakeClientDocument,
  IntakeChatMessage,
  IntakeFaq,
  ClientProgressSummary,
} from '@/types/intake';

/**
 * CRM Portal Service
 *
 * This service is used by CRM clients (not admin users).
 * It uses the CRM API client which authenticates with CRM JWT tokens.
 * All client-specific endpoints automatically use the logged-in client's ID.
 */
class CRMPortalService {
  private getClientId(): string {
    const client = crmAuth.getClient();
    if (!client?.id) {
      throw new Error('No authenticated CRM client');
    }
    return client.id;
  }

  // ==================== PROGRESS & DASHBOARD ====================

  async getProgress(): Promise<ClientProgressSummary> {
    const response = await crmApiClient.get('/crm/portal/progress');
    return response.data;
  }

  // ==================== DOCUMENT TYPES ====================

  async getDocumentTypes(): Promise<{ documentTypes: IntakeDocumentType[]; total: number }> {
    const response = await crmApiClient.get('/crm/portal/document-types');
    return response.data;
  }

  // ==================== DOCUMENTS ====================

  async getDocuments(): Promise<IntakeClientDocument[]> {
    const response = await crmApiClient.get('/crm/portal/documents');
    return response.data;
  }

  async getDocumentUploadUrl(documentTypeId: string, fileName: string, mimeType: string): Promise<{
    uploadUrl: string;
    documentId: string;
    s3Key: string;
  }> {
    const response = await crmApiClient.post('/crm/portal/documents/upload-url', {
      documentTypeId,
      fileName,
      mimeType,
    });
    return response.data;
  }

  async confirmDocumentUpload(documentId: string): Promise<IntakeClientDocument> {
    const response = await crmApiClient.post(`/crm/portal/documents/${documentId}/confirm`);
    return response.data;
  }

  async deleteDocument(documentId: string): Promise<void> {
    await crmApiClient.delete(`/crm/portal/documents/${documentId}`);
  }

  async getDocumentDownloadUrl(documentId: string): Promise<{
    downloadUrl: string;
    fileName: string;
    mimeType: string;
    fileSizeBytes: number;
    uploadedAt: string;
  }> {
    const response = await crmApiClient.get(`/crm/portal/documents/${documentId}/download-url`);
    return response.data;
  }

  // ==================== CHAT ====================

  async getChatMessages(sessionId?: string): Promise<IntakeChatMessage[]> {
    const queryParams = sessionId ? `?sessionId=${sessionId}` : '';
    const response = await crmApiClient.get(`/crm/portal/chat${queryParams}`);
    return response.data;
  }

  async sendChatMessage(content: string): Promise<IntakeChatMessage> {
    const response = await crmApiClient.post('/crm/portal/chat', { content });
    return response.data;
  }

  // ==================== FAQs ====================

  async getFaqs(category?: string): Promise<{ faqs: IntakeFaq[]; total: number }> {
    const queryParams = category ? `?category=${encodeURIComponent(category)}` : '';
    const response = await crmApiClient.get(`/crm/portal/faqs${queryParams}`);
    return response.data;
  }

  // ==================== PROFILE ====================

  async getProfile(): Promise<{
    id: string;
    name: string;
    email: string;
    phone?: string;
    company?: string;
    abn?: string;
    status: string;
    intakeProgress: number;
    tenantName: string;
  }> {
    const response = await crmApiClient.get('/crm/portal/profile');
    return response.data;
  }

  async updateProfile(data: {
    name?: string;
    phone?: string;
    company?: string;
    abn?: string;
  }): Promise<void> {
    await crmApiClient.put('/crm/portal/profile', data);
  }
  // ==================== Client Portal Extensions ====================

  async getSubscription(): Promise<any> {
    const response = await crmApiClient.get('/crm/portal/subscription');
    return response.data;
  }

  async getClientPlans(): Promise<any> {
    const response = await crmApiClient.get('/crm/portal/plans');
    return response.data;
  }

  async getDashboardSummary(): Promise<any> {
    const response = await crmApiClient.get('/crm/portal/dashboard-summary');
    return response.data;
  }

  async getMessages(): Promise<any> {
    const response = await crmApiClient.get('/crm/portal/messages');
    return response.data;
  }

  async sendMessage(content: string): Promise<any> {
    const response = await crmApiClient.post('/crm/portal/messages', { content });
    return response.data;
  }

  async getDeadlines(): Promise<any> {
    const response = await crmApiClient.get('/crm/portal/deadlines');
    return response.data;
  }
  async getFinancials(period?: string): Promise<any> {
    const params = period ? `?period=${period}` : '';
    const response = await crmApiClient.get(`/crm/portal/financials${params}`);
    return response.data;
  }

  async getComplianceStatus(): Promise<any> {
    const response = await crmApiClient.get('/crm/portal/compliance');
    return response.data;
  }

  async sendAIChat(message: string): Promise<any> {
    const response = await crmApiClient.post('/crm/portal/ai-chat', { message });
    return response.data;
  }

  async getAIQuota(): Promise<any> {
    const response = await crmApiClient.get('/crm/portal/ai-quota');
    return response.data;
  }
}

export const crmPortalService = new CRMPortalService();
